/**
 * Derive the displayed countdown from the server-authoritative {@link TimerState}
 * (FR-7.1). The server sends transition events + timestamps, never per-second
 * ticks; the client computes the live remaining seconds from
 * `startedAtEpochMs`/`duration` — there is no client-side countdown *authority*,
 * only a display derived from server truth (DESIGN §7, timer.ts).
 */

import type { TimerState } from '@jeopardy/shared';

/**
 * Live remaining seconds for a timer at wall-clock `nowMs`. When running, counts
 * down from `startedAtEpochMs`; when paused/idle, shows the frozen `timeRemaining`.
 */
export function liveRemainingSeconds(timer: TimerState, nowMs: number): number {
  if (timer.hasExpired) return 0;
  if (!timer.isActive) return timer.timeRemaining;
  if (timer.isPaused || timer.startedAtEpochMs === null) return timer.timeRemaining;
  const elapsedMs = nowMs - timer.startedAtEpochMs;
  const remainingMs = timer.timeRemaining * 1000 - elapsedMs;
  return Math.max(0, Math.ceil(remainingMs / 1000));
}

/** A coarse status band for styling the timer (mirrors the legacy timer states). */
export function timerBand(
  remainingSeconds: number,
  timer: TimerState,
): 'expired' | 'paused' | 'warning' | 'caution' | 'normal' | 'idle' {
  if (timer.hasExpired || (timer.isActive && remainingSeconds <= 0)) return 'expired';
  if (!timer.isActive) return 'idle';
  if (timer.isPaused) return 'paused';
  if (remainingSeconds <= 3) return 'warning';
  if (remainingSeconds <= 7) return 'caution';
  return 'normal';
}
